import startOfWeek from 'date-fns/startOfWeek'
import endOfWeek from 'date-fns/endOfWeek'
import subWeeks from 'date-fns/subWeeks'
import setYear from 'date-fns/setYear'
import setMonth from 'date-fns/setMonth'
import setDate from 'date-fns/setDate'
import {
  GoalHabitsItem,
  GoalHabitsMonthData,
  GoalHabitsType,
  GoalHabitsWeekData,
} from './types'
import format from 'date-fns/format'
import { StatsYearStatus } from 'app/webview/pages/stats_profile/lib/types'

export const getLast10weeks = (yearStatus: StatsYearStatus, year: number) => {
  const weeks = []
  let referenceDate = new Date()

  if (yearStatus === 'past') {
    referenceDate = setYear(new Date(), year)
    referenceDate = setMonth(referenceDate, 11)
    referenceDate = setDate(referenceDate, 31)
  }

  for (let i = 0; i < 10; i++) {
    const start = startOfWeek(subWeeks(referenceDate, i), { weekStartsOn: 0 })
    const start_date = new Date(
      Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())
    )
    const end = endOfWeek(start, { weekStartsOn: 0 })

    weeks.push({
      start_date: start_date,
      end_date: end,
    })
  }

  return weeks
}

export const getAvgWeekly = (
  goals: GoalHabitsWeekData[],
  goalType: GoalHabitsType
) => {
  if (goals.length === 0) return 0

  const totalAchieved = goals.reduce(
    (sum, goal) => sum + goal.number_achieved,
    0
  )
  const average = totalAchieved / goals.length

  if (!Number.isFinite(average)) return 0

  return goalType === 'pages_read'
    ? Math.round(average)
    : parseFloat(average.toFixed(1))
}

export const getAvgMonthly = (
  goals: GoalHabitsMonthData[],
  yearStatus: StatsYearStatus,
  goalType: GoalHabitsType
): number => {
  if (goals.length === 0) return 0

  const total = goals.reduce(
    (sum, entry) => sum + (entry.number_achieved ?? 0),
    0
  )

  const monthsElapsed = yearStatus === 'past' ? 12 : new Date().getMonth() + 1
  const average = total / monthsElapsed

  if (!Number.isFinite(average)) return 0

  return goalType === 'pages_read'
    ? Math.round(average)
    : parseFloat(average.toFixed(1))
}

export const getMonthYAxisIncrement = (maxValue: number): number => {
  if (maxValue < 10) return 1
  if (maxValue < 20) return 2
  if (maxValue < 30) return 5
  if (maxValue < 50) return 10
  if (maxValue < 100) return 20
  if (maxValue < 200) return 50
  if (maxValue < 500) return 100
  if (maxValue < 1000) return 200
  if (maxValue < 5000) return 500
  if (maxValue < 10000) return 1000
  if (maxValue < 50000) return 5000
  return 10000
}

export const transformMonthData = ({
  results = [],
  yearStatus,
  goalType,
}: {
  results: GoalHabitsItem[]
  yearStatus: StatsYearStatus
  goalType: GoalHabitsType
}) => {
  const months = Array.from({ length: 12 }, (_, i) => i)

  const monthData = months.map((month) => {
    const goal = results.find(({ start_at }) => {
      const goalMonth = new Date(start_at).getUTCMonth()
      return goalMonth === month
    })

    return {
      month: format(new Date(2000, month), 'MMM'),
      number_goals: goal?.number_goal ?? 0,
      number_achieved: goal?.number_achieved ?? 0,
    }
  })

  const minValue = 3
  const maxGoalValue = Math.max(
    ...monthData.map((item) => item.number_goals ?? 0)
  )
  const maxAchievedValue = Math.max(
    ...monthData.map((item) => item.number_achieved ?? 0)
  )
  const maxValue = Math.max(maxGoalValue, maxAchievedValue)

  const isZeroState =
    !results.length ||
    !monthData.some((item) => item.number_achieved || item.number_goals)

  return {
    data: monthData || [],
    averageRead: getAvgMonthly(monthData, yearStatus, goalType),
    maxValue: maxValue > minValue ? maxValue : minValue,
    isZeroState,
  }
}

const pagesReadIntervals = [1, 10, 25, 50, 100, 250, 500, 1000, 2000]

const getClosestInterval = (maxValue: number): number => {
  const intervals = pagesReadIntervals
  const calcInterval = Math.floor(maxValue / 4)

  for (let i = intervals.length - 1; i >= 0; i--) {
    if (calcInterval >= intervals[i]) {
      return intervals[i]
    }
  }
  return intervals[0]
}

export const transformWeekData = ({
  results = [],
  yearStatus,
  year,
  goalType,
}: {
  results: GoalHabitsItem[]
  yearStatus: StatsYearStatus
  year: number
  goalType: GoalHabitsType
}) => {
  const last10Weeks = getLast10weeks(yearStatus, year).reverse()

  const normalizeDate = (date: Date) => ({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
    day: date.getUTCDate(),
  })

  const weekData = last10Weeks.map(({ start_date }) => {
    const weekStart = normalizeDate(start_date)

    const goal = results.find(({ start_at }) => {
      const goalStart = normalizeDate(new Date(start_at))

      return (
        goalStart.year === weekStart.year &&
        goalStart.month === weekStart.month &&
        goalStart.day === weekStart.day
      )
    })

    return {
      date: format(start_date, 'MMM d'),
      number_goals: goal?.number_goal ?? 0,
      number_achieved: goal?.number_achieved ?? 0,
    }
  })

  const minValue = 7
  const maxGoalValue = Math.max(...weekData.map((item) => item.number_goals))
  const maxAchievedValue = Math.max(
    ...weekData.map((item) => item.number_achieved)
  )
  const maxValue = Math.max(maxGoalValue, maxAchievedValue)
  const interval = getClosestInterval(maxValue)

  const isZeroState =
    !results.length ||
    !weekData.some((item) => item.number_achieved || item.number_goals)

  return {
    data: weekData || [],
    maxValue: maxValue > minValue ? maxValue : minValue,
    totalRead: getAvgWeekly(weekData, goalType),
    isZeroState,
    yAxisInterval: interval,
  }
}
